import { useState, useEffect } from "react";
import axios from "axios";
import { PlusSquare, Shield, Lock, Settings2, Pencil, Trash2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  inventory_view: { label: "View Inventory", group: "Inventory" },
  inventory_create: { label: "Create Items", group: "Inventory" },
  inventory_edit: { label: "Edit Items", group: "Inventory" },
  inventory_delete: { label: "Delete Items", group: "Inventory" },
  po_view: { label: "View Purchase Orders", group: "Purchase Orders" },
  po_create: { label: "Create Purchase Orders", group: "Purchase Orders" },
  po_approve: { label: "Approve / Reject POs", group: "Purchase Orders" },
  quotation_view: { label: "View Quotations", group: "Quotations" },
  quotation_create: { label: "Create Quotations", group: "Quotations" },
  quotation_edit: { label: "Edit Quotations", group: "Quotations" },
  quotation_delete: { label: "Delete Quotations", group: "Quotations" },
  reports_view: { label: "View Reports", group: "Reports & Security" },
  security_view: { label: "View Security Dashboard", group: "Reports & Security" },
  users_manage: { label: "Manage Users", group: "Administration" },
  downloads_allowed: { label: "Download / Export Files", group: "Downloads" },
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [permissionsDialog, setPermissionsDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editPermissions, setEditPermissions] = useState({});
  const [defaultPermissions, setDefaultPermissions] = useState({});
  const [isCustom, setIsCustom] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "", password: "", role: "staff" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => { fetchUsers(); }, [currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users?page=${currentPage}&page_size=25`);
      const data = response.data;
      setUsers(data.users || data);
      setTotalPages(data.total_pages || 1);
      setTotalUsers(data.total || (data.users || data).length);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/auth/register`, formData);
      toast.success("User created successfully");
      setDialogOpen(false);
      setFormData({ name: "", email: "", password: "", role: "staff" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      fetchUsers();
    } catch (error) { toast.error("Failed to update role"); }
  };

  const handleToggle2FA = async (userId, currentEnforced) => {
    try {
      await axios.post(`${API_URL}/auth/toggle-2fa`, { user_id: userId, enforce_2fa: !currentEnforced });
      toast.success(`2FA ${!currentEnforced ? "enforced" : "disabled"}`);
      fetchUsers();
    } catch (error) { toast.error("Failed to update 2FA"); }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditDialog(true);
  };

  const handleEditUser = async () => {
    try {
      const updates = {};
      if (editName !== selectedUser.name) updates.name = editName;
      if (editEmail !== selectedUser.email) updates.email = editEmail;
      if (!Object.keys(updates).length) { toast.info("No changes"); return; }
      await axios.put(`${API_URL}/users/${selectedUser.id}`, updates);
      toast.success("User updated");
      setEditDialog(false);
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to update user"); }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete user "${user.name}" (${user.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/users/${user.id}`);
      toast.success("User deleted");
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete user"); }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewUserPassword("");
    setPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (newUserPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}/password`, { new_password: newUserPassword });
      toast.success(`Password reset for ${selectedUser.email}`);
      setPasswordDialog(false);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to reset password"); }
  };

  const openPermissionsDialog = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(`${API_URL}/users/${user.id}/permissions`);
      setEditPermissions({ ...response.data.permissions });
      setDefaultPermissions({ ...response.data.default_permissions });
      setIsCustom(response.data.is_custom);
      setPermissionsDialog(true);
    } catch (error) { toast.error("Failed to load permissions"); }
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API_URL}/users/${selectedUser.id}/permissions`, editPermissions);
      toast.success("Permissions updated");
      setPermissionsDialog(false);
      fetchUsers();
    } catch (error) { toast.error("Failed to update permissions"); }
  };

  const handleResetPermissions = async () => {
    try {
      await axios.post(`${API_URL}/users/${selectedUser.id}/permissions/reset`);
      toast.success("Permissions reset to defaults");
      setEditPermissions({ ...defaultPermissions });
      setIsCustom(false);
      fetchUsers();
    } catch (error) { toast.error("Failed to reset permissions"); }
  };

  const togglePermission = (key) => setEditPermissions((prev) => ({ ...prev, [key]: !prev[key] }));

  const groupedPermissions = Object.entries(PERMISSION_LABELS).reduce((acc, [key, meta]) => {
    if (!acc[meta.group]) acc[meta.group] = [];
    acc[meta.group].push({ key, ...meta });
    return acc;
  }, {});

  if (loading) return <div className="p-8"><div className="text-slate-500">Loading users...</div></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-4xl font-black text-slate-900 uppercase page-title" style={{ letterSpacing: "-0.02em" }}>Users</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">{users.length} users in system</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="add-user-btn"><PlusSquare size={18} className="mr-2" /> Add User</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="text-xl font-bold uppercase">Add New User</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 py-4">
              <div><Label className="text-xs font-bold uppercase text-slate-600">Full Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="mt-1 rounded-sm" data-testid="user-name-input" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-600">Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="mt-1 rounded-sm" data-testid="user-email-input" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-600">Password</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required minLength={8} className="mt-1 rounded-sm" data-testid="user-password-input" /></div>
              <div>
                <Label className="text-xs font-bold uppercase text-slate-600">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}><SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger><SelectContent className="bg-white"><SelectItem value="admin">Admin</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent></Select>
              </div>
              <DialogFooter><Button type="submit" className="bg-orange-600 hover:bg-orange-700 text-white rounded-sm" data-testid="create-user-submit">Create User</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 md:px-5 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 md:px-5 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-center px-3 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Role</th>
                <th className="text-center px-3 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">2FA</th>
                <th className="text-center px-3 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 md:px-5 py-4">
                    <p className="font-semibold text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-500 md:hidden">{u.email}</p>
                    {u.permissions && <span className="text-xs text-orange-600 font-medium">Custom permissions</span>}
                  </td>
                  <td className="px-4 md:px-5 py-4 text-slate-600 hidden md:table-cell">{u.email}</td>
                  <td className="px-3 py-4 text-center">
                    <span className={`inline-block px-2 py-1 text-xs font-bold uppercase rounded-sm ${u.role === "admin" ? "bg-orange-100 text-orange-800" : u.role === "manager" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-800"}`}>{u.role}</span>
                  </td>
                  <td className="px-3 py-4 text-center">
                    {u.two_fa_enforced ? <span className="flex items-center justify-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-1 rounded-sm"><Shield size={12} /> On</span> : <span className="text-xs text-slate-400">Off</span>}
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u.id, v)}>
                        <SelectTrigger className="w-24 h-8 text-xs rounded-sm" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white"><SelectItem value="admin">Admin</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(u)} className="h-8 text-xs" title="Edit user" data-testid={`edit-user-${u.id}`}><Pencil size={12} /></Button>
                      <Button size="sm" variant="outline" onClick={() => openPasswordDialog(u)} className="h-8 text-xs" title="Reset password" data-testid={`reset-pwd-${u.id}`}><KeyRound size={12} /></Button>
                      <Button size="sm" variant={u.two_fa_enforced ? "destructive" : "outline"} onClick={() => handleToggle2FA(u.id, u.two_fa_enforced)} className="h-8 text-xs" title="Toggle 2FA" data-testid={`toggle-2fa-${u.id}`}><Lock size={12} /></Button>
                      <Button size="sm" variant="outline" onClick={() => openPermissionsDialog(u)} className="h-8 text-xs" title="Permissions" data-testid={`permissions-${u.id}`}><Settings2 size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteUser(u)} className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete user" data-testid={`delete-user-${u.id}`}><Trash2 size={12} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold uppercase flex items-center gap-2"><Pencil size={18} className="text-orange-500" /> Edit User</DialogTitle></DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-2">
              <div><Label className="text-xs font-bold uppercase text-slate-600">Full Name</Label><Input value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-user-name" /></div>
              <div><Label className="text-xs font-bold uppercase text-slate-600">Email</Label><Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} className="mt-1 rounded-sm" data-testid="edit-user-email" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button><Button onClick={handleEditUser} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-edit-user">Save Changes</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={passwordDialog} onOpenChange={setPasswordDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold uppercase flex items-center gap-2"><KeyRound size={18} className="text-orange-500" /> Reset Password</DialogTitle></DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <p className="font-semibold text-slate-900">{selectedUser.name}</p>
                <p className="text-xs text-slate-500">{selectedUser.email}</p>
              </div>
              <div><Label className="text-xs font-bold uppercase text-slate-600">New Password</Label><Input type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} placeholder="Min 8 characters" minLength={8} className="mt-1 rounded-sm" data-testid="admin-new-password" /></div>
              <DialogFooter><Button variant="outline" onClick={() => setPasswordDialog(false)}>Cancel</Button><Button onClick={handleResetPassword} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="admin-reset-pwd-btn">Reset Password</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialog} onOpenChange={setPermissionsDialog}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-xl font-bold uppercase flex items-center gap-2"><Settings2 size={20} className="text-orange-500" /> Permissions</DialogTitle></DialogHeader>
          {selectedUser && (
            <div className="space-y-5 py-2">
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-sm">
                <p className="font-semibold text-slate-900">{selectedUser.name}</p>
                <p className="text-xs text-slate-500">{selectedUser.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 text-xs font-bold uppercase rounded-sm ${selectedUser.role === "admin" ? "bg-orange-100 text-orange-800" : selectedUser.role === "manager" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-800"}`}>{selectedUser.role}</span>
                  {isCustom && <span className="text-xs text-orange-600 font-medium">Custom</span>}
                </div>
              </div>
              {Object.entries(groupedPermissions).map(([group, perms]) => (
                <div key={group}>
                  <h4 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">{group}</h4>
                  <div className="space-y-2">
                    {perms.map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between py-2 px-3 bg-white border border-slate-100 rounded-sm hover:bg-slate-50 transition-colors">
                        <span className="text-sm text-slate-700">{label}</span>
                        <Switch checked={editPermissions[key] || false} onCheckedChange={() => togglePermission(key)} data-testid={`perm-${key}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2 pt-2">
            {isCustom && <Button variant="outline" onClick={handleResetPermissions} className="text-xs" data-testid="reset-permissions-btn">Reset to Defaults</Button>}
            <Button variant="outline" onClick={() => setPermissionsDialog(false)}>Cancel</Button>
            <Button onClick={handleSavePermissions} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-permissions-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 bg-white border border-slate-200 rounded-sm p-3">
          <p className="text-sm text-slate-500">Page {currentPage} of {totalPages} ({totalUsers} users)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)} data-testid="users-prev-page">Previous</Button>
            <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)} data-testid="users-next-page">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
